// B1012: the Users page's server calls. Every statement is the SQL of
// web/docs/sql.md § Users, roles and permissions, sent on the shared WS.
//
// The listings are `SHOW USERS` (user, roles) and `SHOW ROLES` (role,
// members); both are superuser-only, so a non-root session sees the server's
// `permission denied` in the page, never a fake empty list.

import { wsSql } from './socket.js';

// A SQL string literal: single quotes, an embedded quote doubled. User and
// role names and passwords are all string literals in the grammar
// (`CREATE USER 'alice' PASSWORD 'secret'`, `CREATE ROLE 'analysts'`).
function lit(s) {
  return "'" + String(s).replace(/'/g, "''") + "'";
}

export const userSql = {
  create: (user, password) => `CREATE USER ${lit(user)} PASSWORD ${lit(password)}`,
  setPassword: (user, password) => `ALTER USER ${lit(user)} SET PASSWORD ${lit(password)}`,
  revokeSessions: (user) => `ALTER USER ${lit(user)} REVOKE SESSIONS`,
  drop: (user) => `DROP USER ${lit(user)}`,
};

export const roleSql = {
  create: (role) => `CREATE ROLE ${lit(role)}`,
  drop: (role) => `DROP ROLE ${lit(role)}`,
  grant: (role, user) => `GRANT ROLE ${lit(role)} TO ${lit(user)}`,
  revoke: (role, user) => `REVOKE ROLE ${lit(role)} FROM ${lit(user)}`,
};

// The `roles` / `members` column is a comma-separated, sorted list of names;
// an empty string is no names.
export function splitNames(s) {
  if (!s) return [];
  return String(s).split(',').filter(Boolean);
}

function rowsByName(r) {
  const cols = r?.columns ?? [];
  return (r?.data ?? []).map((row) => {
    const o = {};
    cols.forEach((c, i) => { o[c] = row[i]; });
    return o;
  });
}

export function parseUsers(r) {
  return rowsByName(r).map((o) => ({ user: o.user, roles: splitNames(o.roles) }));
}

export function parseRoles(r) {
  return rowsByName(r).map((o) => ({ role: o.role, members: splitNames(o.members) }));
}

export async function listUsers() { return parseUsers(await wsSql('SHOW USERS')); }
export async function listRoles() { return parseRoles(await wsSql('SHOW ROLES')); }

export async function createUser(user, password) { await wsSql(userSql.create(user, password)); }
export async function setPassword(user, password) { await wsSql(userSql.setPassword(user, password)); }
export async function revokeSessions(user) { await wsSql(userSql.revokeSessions(user)); }
export async function dropUser(user) { await wsSql(userSql.drop(user)); }

export async function createRole(role) { await wsSql(roleSql.create(role)); }
export async function dropRole(role) { await wsSql(roleSql.drop(role)); }
export async function grantRole(role, user) { await wsSql(roleSql.grant(role, user)); }
export async function revokeRole(role, user) { await wsSql(roleSql.revoke(role, user)); }
